import type { FC, JSX } from "react";

import type { IconName } from "@similarweb/icons";
import { TabPanel, TabList, Tab, Tabs } from "@similarweb/ui-components";

import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import categoryService from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { TabContent } from "./components/TabContent";
import { TabPanelItems } from "./components/TabPanelItems";
import { ETabs } from "./types";

const getIsMarketAnalysisLocked = () => isLocked(swSettings?.components?.WebMarketResearchOverviewHome);

interface ITabsProps {
    listItems: any[];
    query: string;
    selectedTab: ETabs;
    setSelectedTab: (tab: ETabs) => void;
    createListItem: (enforceIcon?: boolean, editable?: boolean) => (item: any) => JSX.Element;
    showCustomCategoryEmptyState?: boolean;
    onCreateCustomCategoryClick?: () => void;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
}

export const AutocompleteWebCategoriesTabs: FC<ITabsProps> = ({
    listItems,
    query,
    selectedTab,
    setSelectedTab,
    createListItem,
}) => {
    const className = listItems.length > 0 ? "ListItemsContainer" : "";
    const showCustomCategories = categoryService.hasCustomCategoriesPermission();
    const { PRE_DEFINED, CUSTOM, SHARED } = ETabs;

    const isHideSidebarLockedFeaturesTest = false; // TODO: remove this when cleanup lnx-87-hide-sidebar-locked-features test

    const isMarketAnalysisLocked = getIsMarketAnalysisLocked();
    const showCustomCategoriesWithTest =
        showCustomCategories &&
        (!isHideSidebarLockedFeaturesTest || (isHideSidebarLockedFeaturesTest && !isMarketAnalysisLocked));

    const categorizedItems = listItems.reduce(
        (acc, item) => {
            if (item.isShared) {
                acc[SHARED].push(item);
            } else if (item.domains) {
                acc[CUSTOM].push(item);
            } else {
                acc[PRE_DEFINED].push(item);
            }
            return acc;
        },
        { [CUSTOM]: [], [PRE_DEFINED]: [], [SHARED]: [] },
    );

    const { [PRE_DEFINED]: preDefined, [CUSTOM]: custom, [SHARED]: shared } = categorizedItems;
    // Sort CUSTOM and SHARED by lastUpdated descending
    const sortedCustom = [...custom].sort(
        (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
    );
    const sortedShared = [...shared].sort(
        (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
    );

    const onTabSelected = (index: number) => {
        const tab = index === ETabs.PRE_DEFINED ? "Industries" : "Custom Industries";
        allTrackers.trackEvent("Tab", "click", `Table/${tab}`);
        setSelectedTab(index);
    };

    if (listItems.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            <Tabs
                selectedIndex={selectedTab}
                onSelect={onTabSelected}
            >
                <TabList>
                    <Tab>{`${translate("common.auto.complete.industries")} (${preDefined.length})`}</Tab>
                    {showCustomCategoriesWithTest ? (
                        <Tab isLocked={isMarketAnalysisLocked}>
                            {isMarketAnalysisLocked
                                ? translate("common.auto.complete.markets")
                                : `${translate("common.auto.complete.markets")} (${custom.length})`}
                        </Tab>
                    ) : null}
                    {showCustomCategoriesWithTest ? (
                        <Tab isLocked={isMarketAnalysisLocked}>
                            {isMarketAnalysisLocked
                                ? translate("autocomplete.market.tab.shared.section.empty")
                                : translate("autocomplete.market.tab.shared.section", {
                                      amount: shared.length,
                                  })}
                        </Tab>
                    ) : null}
                </TabList>

                <TabPanel>
                    <TabPanelItems
                        items={preDefined}
                        createListItem={createListItem}
                        enforceIcon={query?.length > 0}
                    />
                </TabPanel>
                <TabPanel>
                    <TabContent
                        isCustom={true}
                        isLocked={isMarketAnalysisLocked}
                        hasItems={custom.length > 0}
                    >
                        <TabPanelItems
                            items={sortedCustom}
                            createListItem={createListItem}
                        />
                    </TabContent>
                </TabPanel>
                <TabPanel>
                    <TabContent
                        isLocked={isMarketAnalysisLocked}
                        hasItems={shared.length > 0}
                    >
                        <TabPanelItems
                            items={sortedShared}
                            isShared={true}
                            createListItem={createListItem}
                        />
                    </TabContent>
                </TabPanel>
            </Tabs>
        </div>
    );
};
